import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { MonoMeta } from "@/design/primitives";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { STATE_WORD } from "./states";

const BRAND_ORDER = [
  "TrustOffice", "AeriusView", "StenoDesk", "True Joy Birthing",
  "WingPoint", "TrustMinutes", "SocializeVideo", "Unsorted",
];

const STATE_SORT = { waiting: 0, alert: 1, broken: 2, stale: 3, quiet: 4, healthy: 5 };

const RAIL_ORDER = ["rail:needs-you", "rail:broken", "rail:stale", "rail:healthy"];

const TONE_DOT = {
  red: "bg-os-red", amber: "bg-os-amber", gold: "bg-os-gold",
  green: "bg-os-green", muted: "bg-os-ink-4", neutral: "bg-os-ink-4",
};

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const ageHrs = (now - d) / 36e5;
  if (ageHrs < 1) return "just now";
  if (ageHrs < 24) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (ageHrs < 48) return "Yesterday " + d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function fmtStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// ── rail button ─────────────────────────────────────────────
function RailBtn({ slug, title, count, tone, active, dashed, onClick }) {
  return (
    <button
      type="button"
      data-testid={`break-rail-${slug}`}
      onClick={onClick}
      className={cn(
        "flex h-8 w-full items-center gap-2 rounded-control px-2 text-left text-[13px]",
        dashed && "border border-dashed border-os-border-strong text-os-ink-4",
        active === slug ? "border border-os-border bg-white font-semibold text-os-ink shadow-[0_1px_3px_rgba(0,0,0,0.04)]" : "text-os-ink-2 hover:bg-black/[0.035]",
      )}
    >
      {tone && <span className={cn("h-[7px] w-[7px] shrink-0 rounded-full", TONE_DOT[tone])} />}
      <span className="truncate">{title}</span>
      {count != null && (
        <span className={cn("ml-auto font-mono text-[11px]", active === slug ? "text-os-ink" : "text-os-ink-3")}>
          {count.toLocaleString("en-US")}
        </span>
      )}
    </button>
  );
}

// ── message row (here = beat row) ─────────────────────────────
function BeatRow({ card, selected, onSelect }) {
  const sw = STATE_WORD[card.state] || STATE_WORD.healthy;
  const track = card.track || [];
  const last = card.lastRunText || fmtWhen(card.lastRunAt);
  return (
    <button
      type="button"
      data-testid="break-beat-row"
      data-state={card.state}
      onClick={() => onSelect(card)}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "w-full border-t border-os-border px-3 py-2.5 text-left first:border-t-0",
        "grid grid-cols-[26px_1fr_auto] items-start gap-x-2",
        selected ? "bg-os-wash shadow-[inset_3px_0_0_0_#FFC838]" : "bg-white hover:bg-os-wash/60",
      )}
    >
      <span className={cn("mt-1.5 h-[7px] w-[7px] rounded-full", TONE_DOT[sw.tone] || "bg-os-ink-4")} />
      <span className="min-w-0">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-[13px] font-semibold text-os-ink">{card.name}</span>
          <span className="shrink-0 rounded border border-os-border px-1 py-px font-mono text-[9px] uppercase tracking-[0.06em] text-os-ink-4">
            {card.brand}
          </span>
        </span>
        <span className="mt-0.5 block truncate text-[12px] leading-snug text-os-ink-3">{card.subtitle}</span>
        {track.length > 0 && (
          <span className="mt-1.5 flex gap-0.5">
            {track.slice(-15).map((t, i) => (
              <span
                key={i}
                className={cn(
                  "h-[5px] w-[5px] rounded-full",
                  t === "clean" && "bg-os-ink-4",
                  t === "failed" && "bg-os-red",
                  t === "missed" && "bg-os-border-strong",
                )}
              />
            ))}
          </span>
        )}
      </span>
      <span className="whitespace-nowrap text-right font-mono text-[10.5px] leading-relaxed text-os-ink-3">
        {last}
        {card.failureStreak > 1 && (
          <span className="mt-1 block text-[10px] font-semibold text-os-red">{card.failureStreak} in a row</span>
        )}
      </span>
    </button>
  );
}

// ── reader pane ───────────────────────────────────────────────
function Reader({ card, board, onNotice }) {
  const [showNote, setShowNote] = useState(false);
  const [noteVal, setNoteVal] = useState("");
  const sw = card ? (STATE_WORD[card.state] || STATE_WORD.healthy) : null;

  if (!card) {
    return (
      <div className="flex flex-1 items-center justify-center border-os-border bg-white p-10 text-center" data-testid="break-reader-empty">
        <p className="max-w-[32ch] text-[0.875rem] leading-relaxed text-os-ink-3">
          Pick a beat to see its details. Kit keeps this list to what needs attention — the full archive stays in the engine.
        </p>
      </div>
    );
  }

  const act = async (verb, extra = {}) => {
    const res = await board.act(verb, card.id, extra.note);
    if (res) onNotice(null);
    else onNotice("That action didn't go through — try again.");
    return res;
  };

  const schedule = card.schedule || "No schedule recorded";
  const last = fmtWhen(card.lastRunAt) || "Not yet";
  const next = fmtWhen(card.nextRunAt) || "Not scheduled";

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="break-reader">
      {/* actions */}
      <div className="flex items-center gap-2 border-b border-os-border px-4 py-2.5">
        <span className="hidden rounded-control border border-os-border-strong bg-white px-3 py-1.5 text-[12px] font-semibold text-os-ink-2 xl:inline-block">
          {sw.word}
        </span>
        <button
          type="button"
          data-testid="break-send-to-kit"
          onClick={() => act("take")}
          className="rounded-control border border-os-border-strong bg-white px-3 py-1.5 text-[12px] font-semibold text-os-ink shadow-[inset_0_1px_0_rgba(255,255,255,0.95)] hover:bg-os-wash"
        >
          Send to Kit
        </button>
        <button
          type="button"
          data-testid="break-note-toggle"
          onClick={() => setShowNote((v) => !v)}
          className="rounded-control border border-os-border bg-white px-3 py-1.5 text-[12px] font-medium text-os-ink-2 hover:bg-os-wash"
        >
          {showNote ? "Close note" : "Note"}
        </button>
        <MonoMeta as="span" className="ml-auto">{card.stamp}</MonoMeta>
      </div>

      {/* note bar */}
      {showNote && (
        <div className="border-b border-os-border bg-os-wash px-4 py-2.5" data-testid="break-note-bar">
          <div className="flex items-center gap-2">
            <input
              value={noteVal}
              onChange={(e) => setNoteVal(e.target.value)}
              placeholder="A note for the record — what this beat is, what happens next"
              className="h-8 flex-1 rounded-control border border-os-border bg-white px-3 text-[12.5px] text-os-ink placeholder:text-os-ink-4"
            />
            <button
              type="button"
              onClick={async () => {
                if (!noteVal.trim()) return;
                const res = await act("note", { note: noteVal.trim() });
                if (res?.ok) { setNoteVal(""); setShowNote(false); }
              }}
              className="rounded-control bg-os-ink px-3 py-1.5 text-[12px] font-semibold text-white"
            >
              Save note
            </button>
          </div>
        </div>
      )}

      {/* head */}
      <div className="border-b border-os-border px-5 pb-3.5 pt-4">
        <div className="flex items-baseline gap-2">
          <h2 className="text-[19px] font-bold leading-snug tracking-[-0.015em] text-os-ink">{card.name}</h2>
          <span className={cn("rounded px-2 py-0.5 text-[11px] font-semibold",
            sw.tone === "red" && "bg-[rgba(194,43,16,0.08)] text-os-red",
            sw.tone === "amber" && "bg-[rgba(196,148,28,0.08)] text-os-amber",
            sw.tone === "green" && "bg-[rgba(21,128,61,0.08)] text-os-green",
            sw.tone === "muted" && "bg-os-wash text-os-ink-3",
          )}>{sw.word}</span>
        </div>
        <div className="mt-2 flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-bronze bg-gold-tint text-[11px] font-bold text-[#8A6A14]">
            {(card.brand || "?").slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-os-ink">
              {card.brand} <span className="font-normal text-os-ink-3">· {schedule}</span>
            </div>
            <div className="font-mono text-[10.5px] text-os-ink-3">{fmtStamp(card.lastRunAt)}</div>
          </div>
        </div>
      </div>

      {/* body — mono facts */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="rounded-card border border-os-border bg-white p-4 shadow-[0_8px_18px_rgba(0,0,0,0.04)]">
          <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">Schedule &amp; last run</div>
          <div className="grid grid-cols-2 gap-y-2 text-[13px] text-os-ink-2">
            <div><span className="text-os-ink-4">Runs:</span> {schedule}</div>
            <div><span className="text-os-ink-4">Last run:</span> {last}</div>
            <div><span className="text-os-ink-4">Next run:</span> {next}</div>
            <div><span className="text-os-ink-4">Streak:</span> {card.failureStreak}</div>
          </div>
        </div>

        {card.annotated && card.problem && (
          <div className="mt-5 max-w-[70ch]">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">Why it exists</div>
            <p className="text-[14px] leading-[1.62] text-os-ink-2">{card.problem}</p>
            {card.fix && <p className="mt-2 text-[14px] leading-[1.62] text-os-ink-2"><span className="text-os-ink-4">Fix:</span> {card.fix}</p>}
          </div>
        )}

        {!card.annotated && (
          <div className="mt-5 max-w-[70ch]">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">Why it exists</div>
            <p className="text-[14px] leading-[1.62] text-os-ink-2">Kit hasn't sorted this one yet — the real job name and brand are above, and the schedule is in the facts block.</p>
          </div>
        )}

        {card.track && card.track.length > 0 && (
          <div className="mt-5 max-w-[70ch]">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">Fail history</div>
            <div className="flex gap-0.5">
              {card.track.map((t, i) => (
                <span
                  key={i}
                  title={t}
                  className={cn(
                    "h-[8px] w-[8px] rounded-full",
                    t === "clean" && "bg-os-ink-4",
                    t === "failed" && "bg-os-red",
                    t === "missed" && "bg-os-border-strong",
                  )}
                />
              ))}
            </div>
            <p className="mt-1 text-[11px] text-os-ink-3">Last 15 runs (newest right)</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── the client ───────────────────────────────────────────────
export function BreakageClient({ items, meta, board, variant = "desktop", onNotice }) {
  const desktop = useMediaQuery("(min-width: 1024px)") && variant === "desktop";
  const rail = items.filter((i) => i.kind === "break-rail" || i.kind === "break-brand");
  const cards = items.filter((i) => i.kind !== "break-rail" && i.kind !== "break-brand");

  const counts = useMemo(() => meta?.counts || {}, [meta]);
  const totalBroken = (counts.broken || 0) + (counts.alert || 0) + (counts.waiting || 0);

  const [active, setActive] = useState("rail:needs-you");
  const [selectedSlug, setSelectedSlug] = useState(null);

  const activeBrand = active?.startsWith("brand:") ? active.slice("brand:".length) : null;

  const visibleCards = useMemo(() => {
    if (active === "rail:needs-you") {
      return cards.filter((c) => ["waiting", "alert", "broken"].includes(c.state));
    }
    if (active === "rail:broken") {
      return cards.filter((c) => c.state === "broken");
    }
    if (active === "rail:stale") {
      return cards.filter((c) => c.state === "stale");
    }
    if (active === "rail:healthy") {
      return cards.filter((c) => ["healthy", "quiet"].includes(c.state));
    }
    if (activeBrand) {
      return cards.filter((c) => c.brand === activeBrand);
    }
    return cards;
  }, [cards, active, activeBrand]);

  const selected = useMemo(
    () => visibleCards.find((c) => c.id === selectedSlug) || visibleCards[0] || null,
    [visibleCards, selectedSlug],
  );

  // Context header text
  const contextHeader = useMemo(() => {
    if (active === "rail:needs-you") {
      return `${totalBroken} broken of ${counts.total || cards.length} beats`;
    }
    if (active === "rail:broken") {
      return `${counts.broken || 0} broken`;
    }
    if (active === "rail:stale") {
      return `${counts.stale || 0} need sorting`;
    }
    if (active === "rail:healthy") {
      return `${(counts.healthy || 0) + (counts.quiet || 0)} healthy & quiet`;
    }
    if (activeBrand) {
      const brandBroken = cards.filter((c) => c.brand === activeBrand && ["broken", "alert", "waiting"].includes(c.state)).length;
      const brandTotal = cards.filter((c) => c.brand === activeBrand).length;
      return `${activeBrand} — ${brandBroken} broken of ${brandTotal} beats`;
    }
    return "";
  }, [active, activeBrand, counts, cards, totalBroken]);

  return (
    <div data-testid="breakage-client" className="w-full text-os-ink">
      {/* top strip */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <h1 className="text-[15px] font-bold tracking-[-0.01em] text-os-ink">Breakage</h1>
        <MonoMeta data-testid="break-as-of" as="p" className="whitespace-nowrap">
          counts from {fmtStamp(meta?.source_stamp || meta?.ingested_at)}
        </MonoMeta>
        <span className="ml-auto rounded border border-os-border bg-os-wash px-2 py-1 font-mono text-[10.5px] text-os-ink-2">
          {counts.broken || 0} broken · {" "}
          {counts.alert || 0} alerts · {" "}
          {counts.stale || 0} need sorting
        </span>
      </div>

      <div
        className={cn(
          "grid min-h-[560px] overflow-hidden rounded-card border border-os-border",
          desktop ? "grid-cols-[230px_430px_1fr]" : "grid-cols-1",
        )}
        style={{ height: desktop ? "min(78vh, 860px)" : undefined }}
      >
        {/* pane 1 — rail */}
        <aside className="hidden min-h-0 flex-col overflow-y-auto border-r border-os-border bg-os-wash p-2.5 md:flex">
          <div className="px-1.5 pb-1.5 pt-1 font-mono text-[10px] uppercase tracking-[0.09em] text-os-ink-4">
            Needs you
          </div>
          {rail
            .filter((r) => RAIL_ORDER.includes(r.slug))
            .map((r) => (
              <RailBtn
                key={r.slug}
                slug={r.slug}
                title={r.payload.title}
                count={r.payload.count}
                tone={r.payload.tone}
                active={active}
                onClick={() => { setActive(r.slug); setSelectedSlug(null); }}
              />
            ))}
          <div className="mx-1.5 my-2.5 h-px bg-os-border" />
          <div className="px-1.5 pb-1.5 font-mono text-[10px] uppercase tracking-[0.09em] text-os-ink-4">
            Brands
          </div>
          {BRAND_ORDER.map((b) => {
            const card = rail.find((r) => r.slug === `brand:${b}`);
            if (!card) return null;
            return (
              <RailBtn
                key={b}
                slug={card.slug}
                title={card.payload.title}
                count={card.payload.count}
                tone={card.payload.tone}
                active={active}
                onClick={() => { setActive(card.slug); setSelectedSlug(null); }}
              />
            );
          })}
          <div className="mx-1.5 my-2.5 h-px bg-os-border" />
          <div className="px-1.5 pb-1.5 font-mono text-[10px] uppercase tracking-[0.09em] text-os-ink-4">
            Healthy &amp; quiet
          </div>
          <RailBtn
            slug="rail:healthy"
            title="Healthy & quiet"
            count={(counts.healthy || 0) + (counts.quiet || 0)}
            tone="green"
            active={active}
            onClick={() => { setActive("rail:healthy"); setSelectedSlug(null); }}
          />
          <div className="px-1.5 pb-1 pt-2 text-[10.5px] leading-relaxed text-os-ink-4">
            One pool across every brand — each beat's real name and schedule live in the facts block.
          </div>
        </aside>

        {/* pane 2 — beat list */}
        <section className={cn("flex min-h-0 flex-col border-os-border bg-white md:border-r", !desktop && "hidden")} data-testid="break-list">
          <div className="flex h-11 items-center gap-2.5 border-b border-os-border px-3.5">
            <span className="text-[14px] font-semibold text-os-ink">{contextHeader}</span>
            <span className="font-mono text-[10px] text-os-ink-3">{active === "rail:healthy" ? "name asc" : "streak desc"}</span>
            <span className="ml-auto font-mono text-[10px] text-os-ink-4">
              {visibleCards.length} shown
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {active === "rail:stale" && (
              <p className="border-b border-os-border bg-os-wash px-3.5 py-2 text-[11.5px] leading-snug text-os-ink-2">
                Paused or disabled beats — Kit hasn't sorted these yet. The real name and brand are still visible above.
              </p>
            )}
            {visibleCards.length === 0 && (
              <div className="p-6 text-center text-[13px] leading-relaxed text-os-ink-3" data-testid="break-list-empty">
                Nothing in this view right now — the list fills on Kit's next sync.
              </div>
            )}
            <div>
              {visibleCards.map((c) => (
                <BeatRow key={c.id} card={c} selected={selected?.id === c.id} onSelect={(x) => setSelectedSlug(x.id)} />
              ))}
            </div>
          </div>
        </section>

        {/* pane 3 — reader */}
        <Reader card={selected} board={board} onNotice={onNotice} />
      </div>
    </div>
  );
}
